import { SelectOption } from '../../../../types'

/** Time window in ms after which type-ahead search buffer is reset. */
export const TYPE_AHEAD_RESET_MS = 500

/**
 * Normalises a single value or array into an array of selected values (for multi-select).
 */
export const toArray = (val: string | number | string[] | number[] | undefined): (string | number)[] => {
    if (Array.isArray(val)) return val
    if (val != null) return [val]
    return []
}

/**
 * Normalises defaultValue into a single selected value (for single-select).
 */
export const toSingleValue = (value: string | number | string[] | number[] | undefined): string | number | null => {
    if (value == null) return null
    if (Array.isArray(value)) return value.length > 0 ? value[0] : null
    return value
}

/**
 * Returns whether the given value is in the selected array (for multi-select option state).
 */
export const isOptionSelected = (selected: (string | number)[], value: string | number | null): boolean =>
    selected.some((selectedValue) => selectedValue == value)

/**
 * Finds the next option keyboard focus may land on. Disabled options stay visible, but should not receive active focus.
 */
export const findNextEnabledOptionIndex = (options: SelectOption[], startIndex: number): number => {
    if (options.length === 0) return 0
    const safeStart = Math.min(Math.max(0, startIndex), options.length - 1)
    for (let index = safeStart; index < options.length; index += 1) {
        if (!options[index]?.disabled) return index
    }
    for (let index = 0; index < safeStart; index += 1) {
        if (!options[index]?.disabled) return index
    }
    return safeStart
}

/**
 * Finds the previous option keyboard focus may land on. Wraps so ArrowUp/End can skip disabled trailing options.
 */
export const findPreviousEnabledOptionIndex = (options: SelectOption[], startIndex: number): number => {
    if (options.length === 0) return 0
    const safeStart = Math.min(Math.max(0, startIndex), options.length - 1)
    for (let index = safeStart; index >= 0; index -= 1) {
        if (!options[index]?.disabled) return index
    }
    for (let index = options.length - 1; index > safeStart; index -= 1) {
        if (!options[index]?.disabled) return index
    }
    return safeStart
}

/**
 * Finds the first enabled option whose label starts with the search string (case-insensitive).
 * Used for type-ahead in the combobox. Wraps from end to start and falls back to an enabled option.
 */
export const findOptionIndexByLabel = (options: SelectOption[], search: string, startIndex: number): number => {
    if (options.length === 0) return 0
    const transformedSearch = search.toLowerCase()
    const safeStart = Math.min(Math.max(0, startIndex), options.length - 1)
    for (let index = safeStart; index < options.length; index += 1) {
        if (!options[index]?.disabled && options[index]?.label?.toLowerCase().startsWith(transformedSearch)) return index
    }
    for (let index = 0; index < safeStart; index += 1) {
        if (!options[index]?.disabled && options[index]?.label?.toLowerCase().startsWith(transformedSearch)) return index
    }
    return findNextEnabledOptionIndex(options, safeStart)
}
